<template>
  <LoaderOverlay v-if="loading" />
  <form class="searchbox" @submit.prevent="handleSearch">
    <div class="form-group">
      <label>Départ</label>
      <div class="dropdown-wrapper">
        <input v-model="startCityDisplay"
          @input="startSuggestions = filterCities(startCityDisplay); form.value.startCity = ''"
          @focus="startSuggestions = citiesStore.cities" @blur="startSuggestions = []" type="text"
          placeholder="D'où partez-vous?" required>
        <ul v-if="startSuggestions.length" class="suggestions">
          <li v-for="city in startSuggestions" :key="city.uuid" @mousedown="selectCity('startCity', city)">
            {{ city.name }}
          </li>
        </ul>
      </div>
    </div>

    <div class="form-group">
      <label>Arrivée</label>
      <div class="dropdown-wrapper">
        <input v-model="endCityDisplay" @input="endSuggestions = filterCities(endCityDisplay); form.value.endCity = ''"
          @focus="endSuggestions = citiesStore.cities" @blur="endSuggestions = []" type="text"
          placeholder="Où allez-vous?" required>
        <ul v-if="endSuggestions.length" class="suggestions">
          <li v-for="city in endSuggestions" :key="city.uuid" @mousedown="selectCity('endCity', city)">
            {{ city.name }}
          </li>
        </ul>
      </div>
    </div>

    <div class="form-group date-group">
      <label>Date</label>
      <input v-model="form.dateToStart" type="date" :min="minDate" required>
    </div>

    <button type="submit" class="btn btn-primary btn-full">Rechercher</button>
  </form>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useSearchStore } from '@/stores/searchStore'
import { useCitiesStore } from '@/stores/citiesStore'
import { sleep } from '@/utils/sleep'
import LoaderOverlay from './LoaderOverlay.vue'
import { config } from '@/config/env.js'

const props = defineProps({
  startCity: {
    type: String,
    default: ''
  },
  endCity: {
    type: String,
    default: ''
  },
  date: {
    type: String,
    default: ''
  }
})

const router = useRouter()
const route = useRoute()
const searchStore = useSearchStore()
const citiesStore = useCitiesStore()

const today = new Date().toISOString().split('T')[0]

const form = ref({
  startCity: '',
  endCity: '',
  dateToStart: today
})

const startCityDisplay = ref('')
const endCityDisplay = ref('')

const loading = ref(false)
const error = ref(null)
const startSuggestions = ref([])
const endSuggestions = ref([])

const minDate = computed(() => today)

function normalizeDate(value) {
  if (!value) return today
  return String(value).split('T')[0]
}

function filterCities(query) {
  if (!query || query.length < 1) return citiesStore.cities
  const lower = query.toLowerCase()
  return citiesStore.cities.filter(city =>
    city.name.toLowerCase().includes(lower)
  )
}

function findBestCity(value) {
  if (!value) return null

  const needle = value.trim().toLowerCase()
  if (!needle) return null

  const exact = citiesStore.cities.find(
    city => city.name.trim().toLowerCase() === needle
  )
  if (exact) return exact

  const startsWith = citiesStore.cities.find(
    city => city.name.trim().toLowerCase().startsWith(needle)
  )
  if (startsWith) return startsWith

  return citiesStore.cities.find(
    city => city.name.trim().toLowerCase().includes(needle)
  ) || null
}

function resolveCitySlug(value) {
  const city = findBestCity(value)
  return city ? city.slug : ''
}

function resolveCityName(value) {
  const city = findBestCity(value)
  return city ? city.name : value || ''
}

function selectCity(field, city) {
  if (field === 'startCity') {
    form.value.startCity = city.slug
    startCityDisplay.value = city.name
    startSuggestions.value = []
  }
  if (field === 'endCity') {
    form.value.endCity = city.slug
    endCityDisplay.value = city.name
    endSuggestions.value = []
  }
}

function hydrateForm() {
  const rawStartCity =
    props.startCity ||
    route.params.startCity ||
    route.query.startCity ||
    searchStore.searchParams.startCity ||
    ''

  const rawEndCity =
    props.endCity ||
    route.params.endCity ||
    route.params.arrived ||
    route.query.endCity ||
    searchStore.searchParams.endCity ||
    ''

  const rawDate =
    props.date ||
    route.params.date ||
    route.query.dateToStart ||
    searchStore.searchParams.dateToStart ||
    today

  form.value.startCity = resolveCitySlug(rawStartCity)
  form.value.endCity = resolveCitySlug(rawEndCity)
  form.value.dateToStart = normalizeDate(rawDate)
  startCityDisplay.value = resolveCityName(rawStartCity)
  endCityDisplay.value = resolveCityName(rawEndCity)
}

onMounted(async () => {
  await citiesStore.fetchCities()
  hydrateForm()
})

watch(
  () => [props.startCity, props.endCity, props.date, route.params, route.query],
  () => {
    if (!citiesStore.cities.length) return
    hydrateForm()
  },
  { immediate: true, deep: true }
)

async function handleSearch() {
  if (!form.value.startCity || !form.value.endCity || !form.value.dateToStart) {
    error.value = 'Tous les champs sont requis'
    return
  }

  loading.value = true
  error.value = null
  const minTime = sleep(800)

  try {
    const dateOnly = normalizeDate(form.value.dateToStart)

    const payload = {
      startCity: form.value.startCity,
      endCity: form.value.endCity,
      dateToStart: `${dateOnly}T00:00:00`,
      dateToEnd: `${dateOnly}T23:59:59`
    }

    const response = await fetch(config.apiUrl + '/trips/search', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => null)
      throw new Error(errorData?.message || `Erreur ${response.status}`)
    }

    const data = await response.json()

    searchStore.setSearchResults(Array.isArray(data) ? data : [], {
      ...form.value,
      dateToStart: dateOnly
    })

    await minTime
    await router.push({
      name: 'searchFromToCityDate',
      params: {
        startCity: form.value.startCity,
        endCity: form.value.endCity,
        date: dateOnly
      }
    })
    // await router.push({
    //   name: 'search',
    //   query: {
    //     startCity: form.value.startCity,
    //     endCity: form.value.endCity,
    //     dateToStart: dateOnly
    //   }
    // })
  } catch (e) {
    error.value = e.message || 'Erreur lors de la recherche'
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
.searchbox {
  background: var(--bg-primary);
  color: var(--color-grey-dark);
  padding: 2rem;
  border-radius: var(--border-radius-md);
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  box-shadow: var(--shadow-md);
  width: 100%;
}

.searchbox label {
  margin: 0;
}

.searchbox button {
  height: 46px;
  align-self: end;
}

.suggestions li {
  font-size: 1.0rem;
}

/* Media query mobile */
@media (max-width: 768px) {
  .searchbox {
    grid-template-columns: 1fr;
  }
}
</style>
